const SHA256_CONSTANTS = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5,
  0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
  0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc,
  0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7,
  0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3,
  0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5,
  0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
  0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
])

const rotateRight = (value: number, amount: number): number => (
  (value >>> amount) | (value << (32 - amount))
)

const utf8Bytes = (value: string): Uint8Array => {
  let byteLength = 0
  for (const character of value) {
    const codePoint = character.codePointAt(0) || 0
    byteLength += codePoint <= 0x7f ? 1 : codePoint <= 0x7ff ? 2 : codePoint <= 0xffff ? 3 : 4
  }

  const bytes = new Uint8Array(byteLength)
  let offset = 0
  for (const character of value) {
    const codePoint = character.codePointAt(0) || 0
    if (codePoint <= 0x7f) {
      bytes[offset] = codePoint
      offset += 1
    } else if (codePoint <= 0x7ff) {
      bytes[offset] = 0xc0 | (codePoint >>> 6)
      bytes[offset + 1] = 0x80 | (codePoint & 0x3f)
      offset += 2
    } else if (codePoint <= 0xffff) {
      bytes[offset] = 0xe0 | (codePoint >>> 12)
      bytes[offset + 1] = 0x80 | ((codePoint >>> 6) & 0x3f)
      bytes[offset + 2] = 0x80 | (codePoint & 0x3f)
      offset += 3
    } else {
      bytes[offset] = 0xf0 | (codePoint >>> 18)
      bytes[offset + 1] = 0x80 | ((codePoint >>> 12) & 0x3f)
      bytes[offset + 2] = 0x80 | ((codePoint >>> 6) & 0x3f)
      bytes[offset + 3] = 0x80 | (codePoint & 0x3f)
      offset += 4
    }
  }
  return bytes
}

export class StreamingSha256 {
  private readonly hash = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
    0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ])
  private readonly words = new Uint32Array(64)
  private readonly pending = new Uint8Array(64)
  private pendingLength = 0
  private bytesHashed = 0
  private finalized = false

  private processBlock(source: Uint8Array, offset: number): void {
    const words = this.words
    const view = new DataView(
      source.buffer,
      source.byteOffset + offset,
      64,
    )
    for (let index = 0; index < 16; index += 1) {
      words[index] = view.getUint32(index * 4, false)
    }
    for (let index = 16; index < 64; index += 1) {
      const left = words[index - 15]
      const right = words[index - 2]
      const sigma0 = rotateRight(left, 7) ^ rotateRight(left, 18) ^ (left >>> 3)
      const sigma1 = rotateRight(right, 17) ^ rotateRight(right, 19) ^ (right >>> 10)
      words[index] = (words[index - 16] + sigma0 + words[index - 7] + sigma1) >>> 0
    }

    let a = this.hash[0]
    let b = this.hash[1]
    let c = this.hash[2]
    let d = this.hash[3]
    let e = this.hash[4]
    let f = this.hash[5]
    let g = this.hash[6]
    let h = this.hash[7]
    for (let index = 0; index < 64; index += 1) {
      const sum1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25)
      const choice = (e & f) ^ (~e & g)
      const temporary1 = (h + sum1 + choice + SHA256_CONSTANTS[index] + words[index]) >>> 0
      const sum0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22)
      const majority = (a & b) ^ (a & c) ^ (b & c)
      const temporary2 = (sum0 + majority) >>> 0
      h = g
      g = f
      f = e
      e = (d + temporary1) >>> 0
      d = c
      c = b
      b = a
      a = (temporary1 + temporary2) >>> 0
    }

    this.hash[0] = (this.hash[0] + a) >>> 0
    this.hash[1] = (this.hash[1] + b) >>> 0
    this.hash[2] = (this.hash[2] + c) >>> 0
    this.hash[3] = (this.hash[3] + d) >>> 0
    this.hash[4] = (this.hash[4] + e) >>> 0
    this.hash[5] = (this.hash[5] + f) >>> 0
    this.hash[6] = (this.hash[6] + g) >>> 0
    this.hash[7] = (this.hash[7] + h) >>> 0
  }

  update(bytes: Uint8Array): this {
    if (this.finalized) {
      throw new Error('SHA-256 digest is already finalized.')
    }
    this.bytesHashed += bytes.byteLength
    let offset = 0

    if (this.pendingLength > 0) {
      const copied = Math.min(64 - this.pendingLength, bytes.byteLength)
      this.pending.set(bytes.subarray(0, copied), this.pendingLength)
      this.pendingLength += copied
      offset += copied
      if (this.pendingLength === 64) {
        this.processBlock(this.pending, 0)
        this.pendingLength = 0
      }
    }

    while (offset + 64 <= bytes.byteLength) {
      this.processBlock(bytes, offset)
      offset += 64
    }
    if (offset < bytes.byteLength) {
      const remainder = bytes.subarray(offset)
      this.pending.set(remainder, 0)
      this.pendingLength = remainder.byteLength
    }
    return this
  }

  hexDigest(): string {
    if (this.finalized) {
      throw new Error('SHA-256 digest is already finalized.')
    }
    this.finalized = true
    const finalLength = this.pendingLength < 56 ? 64 : 128
    const finalBlock = new Uint8Array(finalLength)
    finalBlock.set(this.pending.subarray(0, this.pendingLength))
    finalBlock[this.pendingLength] = 0x80
    const bitLength = this.bytesHashed * 8
    const view = new DataView(finalBlock.buffer)
    view.setUint32(
      finalLength - 8,
      Math.floor(bitLength / 0x100000000),
      false,
    )
    view.setUint32(finalLength - 4, bitLength >>> 0, false)
    for (let offset = 0; offset < finalLength; offset += 64) {
      this.processBlock(finalBlock, offset)
    }
    return Array.from(
      this.hash,
      word => word.toString(16).padStart(8, '0'),
    ).join('')
  }
}

export const sha256Hex = (value: string): string =>
  new StreamingSha256().update(utf8Bytes(value)).hexDigest()
